import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, ChevronRight } from "lucide-react";
import SiteShell from "@/components/SiteShell";
import SEO from "@/components/SEO";
import FranchiseForm from "@/components/FranchiseForm";
import NotFound from "@/pages/NotFound";
import { FRANCHISE_PCD, MANUFACTURING, PRODUCT_SEO, stateConfig, cityConfig, RELATED_LINKS } from "@/data/landingData";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://saanswellness.com";

function LandingTemplate({ cfg, path }) {
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Service", name: cfg.h1,
      description: cfg.metaDescription, provider: { "@type": "Organization", name: "Saans Wellness" }, areaServed: "IN",
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: cfg.faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: (cfg.breadcrumb || [["Home", "/"], [cfg.h1, path]]).map(([n, u], i) => ({
        "@type": "ListItem", position: i + 1, name: n, item: ORIGIN + u,
      })),
    },
  ];

  return (
    <SiteShell>
      <SEO title={cfg.metaTitle} description={cfg.metaDescription} path={path} image={cfg.image} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#f9f8f5] pt-32 pb-16 md:pt-40 md:pb-20" data-testid="landing-hero">
        <div className="pointer-events-none absolute -right-40 -top-24 h-[420px] w-[420px] rounded-full bg-[#f2ece4] blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 md:px-8 lg:grid-cols-2">
          <div>
            <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-[#8a8880]">
              <Link to="/" className="hover:text-[#d46a1d]">Home</Link>
              {(cfg.breadcrumb || [[cfg.eyebrow, path]]).map(([n, u], i) => (
                <span key={i} className="flex items-center gap-1"><ChevronRight size={12} /><Link to={u} className="hover:text-[#d46a1d]">{n}</Link></span>
              ))}
            </nav>
            <div className="eyebrow text-[#d46a1d]">{cfg.eyebrow}</div>
            <h1 className="mt-3 font-serif text-4xl leading-[1.05] tracking-tight text-[#202124] md:text-5xl">{cfg.h1}</h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#55534e]">{cfg.intro}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#franchise" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#d46a1d] px-7 py-3.5 text-base font-semibold text-white transition-transform hover:scale-[1.03]" data-testid="landing-cta">
                Enquire Now <ArrowRight size={18} />
              </a>
              <Link to="/" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#d8cfc1] bg-white px-7 py-3.5 text-base font-semibold text-[#202124] hover:border-[#d46a1d] hover:text-[#d46a1d]">
                Explore Saans Wellness
              </Link>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="overflow-hidden rounded-[1.75rem] card-shadow-lg">
            <img src={cfg.image} alt={cfg.h1} loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Bullets */}
      <section className="bg-[#f2ece4] py-16">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cfg.bullets.map((b) => (
              <div key={b} className="flex items-start gap-3 rounded-2xl border border-[#e7e0d6] bg-white p-5 card-shadow">
                <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#1f5d3a]" />
                <span className="text-[15px] text-[#3a3a37]">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="bg-[#f9f8f5] py-16 md:py-20">
        <div className="mx-auto max-w-3xl space-y-10 px-5 md:px-8">
          {cfg.sections.map(([h, p]) => (
            <div key={h}>
              <h2 className="font-serif text-3xl text-[#202124]">{h}</h2>
              <p className="mt-3 text-[16px] leading-relaxed text-[#55534e]">{p}</p>
            </div>
          ))}
          {/* FAQ */}
          <div>
            <h2 className="font-serif text-3xl text-[#202124]">Frequently asked</h2>
            <div className="mt-4 space-y-4">
              {cfg.faqs.map(([q, a]) => (
                <div key={q} className="rounded-xl border border-[#e7e0d6] bg-white p-5 card-shadow">
                  <h3 className="font-serif text-lg text-[#202124]">{q}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#55534e]">{a}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Internal links */}
          <div>
            <h2 className="font-serif text-2xl text-[#202124]">Explore more</h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {RELATED_LINKS.filter(([, u]) => u !== path).map(([n, u]) => (
                <Link key={u} to={u} className="rounded-full border border-[#d8cfc1] bg-white px-4 py-2 text-sm font-medium text-[#4a4a48] hover:border-[#d46a1d] hover:text-[#d46a1d]">{n}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FranchiseForm />
    </SiteShell>
  );
}

export default function LandingRoute() {
  const { state, city, kind, type } = useParams();
  const { pathname } = useLocation();

  let cfg = null;
  if (pathname === "/franchise/ayurvedic-pcd") cfg = FRANCHISE_PCD;
  else if (kind) cfg = MANUFACTURING[kind];
  else if (type) cfg = PRODUCT_SEO[type];
  else if (state && city) cfg = cityConfig(state, city);
  else if (state) cfg = stateConfig(state);

  if (!cfg) return <NotFound />;
  return <LandingTemplate cfg={cfg} path={pathname} />;
}
